import React, { useRef, useState } from 'react';
import { useDeleteAiMedia } from '../../../t2c-fe/services';
import { useSnackbar } from 'notistack';
import { useDialog } from '../useDialog';

import { useRouter } from 'next/router';
import { AIMedia, AIMediaStatus } from '../../../t2c-fe/@types';
import { useUpdateAiMedia } from '../../../t2c-fe/services/ai-generate/updateAiMedia';
import { handleDownloadVideoFile } from '../../../t2c-fe/utils';

export const useGeneratedMusicSection = ({ params, setParams, refetchData }) => {
  const { push } = useRouter();
  const { mutate: renameMusic, isLoading: isRenamingMusic } = useUpdateAiMedia();
  const { mutate: deleteMusic, isLoading: isDeletingMusic } = useDeleteAiMedia();

  const {
    openDialog: openDeleteModal,
    closeDialog: closeDeleteModal,
    isDialogOpen: isOpenDeleteModal,
  } = useDialog();

  const {
    openDialog: openRenameModal,
    closeDialog: closeRenameModal,
    isDialogOpen: isOpenRenameModal,
  } = useDialog();

  const handleViewAll = () => {
    push('/music');
  };

  const filterOptions = [
    {
      id: 1,
      name: 'In queue',
      value: AIMediaStatus.NotStarted,
    },
    {
      id: 2,
      name: 'Generating',
      value: AIMediaStatus.InProgress,
    },
    {
      id: 3,
      name: 'Generated',
      value: AIMediaStatus.Success,
    },
    {
      id: 4,
      name: 'Failed',
      value: AIMediaStatus.Error,
    },
  ];

  const handleFilterOptionChange = (val: AIMediaStatus) => {
    setParams({ ...params, filters: { status: val } });
  };

  const handleShowMore = () => {
    setParams({ ...params, limit: params.limit + 10 });
  };

  const handleOnClick = (id: string) => push(`/music/${id}`);

  const handleOnCopy = (prompt: string) => {
    navigator.clipboard
    .writeText(prompt || '')
    .then(() => {
      enqueueSnackbar('Text copied to clipboard!', { variant: 'success' });
    })
    .catch(() => {
      enqueueSnackbar('Failed to copy text', { variant: 'error' });
    });
  };

  const menuActions = [
    {
      id: 'preview',
      label: 'Preview',
      // imageIcon: '/assets/icons/View.svg',
      isHidden: status !== AIMediaStatus.Success,
      iconifyIcon: 'mdi:eye-outline',
      onClick: (data: AIMedia) => {
        push(`/music/${data.id}`);
      },
    },
    {
      id: 'rename',
      label: 'Rename',
      // imageIcon: '/assets/icons/RenameMenu.svg',
      iconifyIcon: 'mdi:rename',
      onClick: (data: AIMedia) => {
        handleOpenRenameModal(data);
      },
    },
    {
      id: 'copyPrompt',
      label: 'Copy Prompt',
      iconifyIcon: 'mdi:content-copy',
      onClick: (data: AIMedia) => {
        handleOnCopy(data.prompt);
      },
    },
    {
      id: 'editPrompt',
      label: 'Edit Prompt',
      iconifyIcon: 'mdi:clipboard-edit-outline',
      onClick: (data: AIMedia) => {
        push(`/music?edit=${data.id}`);
      },
    },
    {
      id: 'downloadVideo',
      label: 'Download',
      // imageIcon: '/assets/icons/DownloadMenu.svg',
      iconifyIcon: 'material-symbols:download',
      onClick: (data: AIMedia) =>
        handleDownloadVideoFile({ name: data.name, url: data.url, format: 'audio' }),
    },
    {
      id: 'deleteVideo',
      label: 'Delete',
      // imageIcon: '/assets/icons/DeleteMenu.svg',
      onClick: (data: AIMedia) => handleOpenDeleteModal(data),
      iconifyIcon: 'material-symbols:delete',
    },
  ];

  const { enqueueSnackbar } = useSnackbar();

  const handleOnRename = (name: string) => {
    renameMusic(
      { mediaId: selectedMusicData?.id, name },
      {
        onSuccess: () => {
          enqueueSnackbar('Name updated successfully!', { variant: 'success' });
          closeRenameModal();
          refetchData();
        },
        onError: (error: any) => {
          enqueueSnackbar(error?.message || 'Failed to update name. Please try again.', {
            variant: 'error',
          });
          closeRenameModal();
        },
      }
    );
  };

  const confirmDeleteMusic = async () => {
    deleteMusic(
      { aiMediaId: selectedMusicData.id },
      {
        onSuccess: () => {
          refetchData();
          enqueueSnackbar('Video deleted successfully', { variant: 'success' });
          closeDeleteModal();
        },
        onError: () => {
          enqueueSnackbar('Error deleting video', { variant: 'error' });
        },
      }
    );
  };

  const handleUpgrade = () => push('/subscription');

  const [selectedMusicData, setSelectedMusicData] = useState<AIMedia>(null);

  const handleOpenRenameModal = (data: AIMedia) => {
    setSelectedMusicData(data);
    openRenameModal();
  };
  const handleOpenDeleteModal = (data: AIMedia) => {
    setSelectedMusicData(data);
    openDeleteModal();
  };
  return {
    handleViewAll,
    filterOptions,
    handleFilterOptionChange,
    menuActions,
    handleUpgrade,
    handleShowMore,
    handleOnClick,
    isOpenRenameModal,
    closeRenameModal,
    openRenameModal,
    handleOnRename,
    isOpenDeleteModal,
    closeDeleteModal,
    confirmDeleteMusic,
    isDeletingMusic,
    selectedMusicData,
    isRenamingMusic,
  };
};
